import React, { useMemo, useCallback, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ControlledOverflow from '@mlb/react-patterns/v4/components/ControlledOverflow/controlledOverflow';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useTracking from '@gameday/utils/hooks/useTracking';
import GameStoryThumbnail from '../GameStoryThumbnail/GameStoryThumbnail';
import VideoPlayer from '@gameday/components/VideoPlayer/VideoPlayer';
import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';
import changeViewAction from '@gameday/app/lib/store/actions/views';

import {
    VideoCarouselWrapper,
    StoryWrapper,
    FilmRoomLinkWrapper,
    StoryInfo,
    StoryText,
    SkeletonWrapper,
    StorySkeletonWrapper,
    LinkSkeletonWrapper,
    TitleWrapper,
    SeeAllButton,
    HeaderWrapper
} from './VideoCarousel.style';
import { getFilmRoomGameUrl } from 'bdata-film-room-utils';
import { withTheme } from 'styled-components';

const CARDWIDTH = 362;
const CARDWIDTH_DEFAULT = 148;

const generateStorySlide = (storyUrl, handlersStoryUrl, gameStoryText, home, away) => {
    return (
        <StoryWrapper
            url={`https://${storyUrl}`}
            target="_blank"
            handlers={handlersStoryUrl}
            key={'story-slide'}
        >
            <GameStoryThumbnail home={home} away={away} />
            <StoryInfo>
                <StoryText>
                    <div>{gameStoryText}</div>
                </StoryText>
            </StoryInfo>
        </StoryWrapper>
    );
};

const generateLinkSlide = (isLoading, link, linkMoreText) => {
    return isLoading
        ? (
            <LinkSkeletonWrapper key={'skeleton-slide-flimlink'}>
                <Skeleton />
            </LinkSkeletonWrapper>
        )
        : (
            <FilmRoomLinkWrapper
                url={link}
                target="_blank"
                key={'skeleton-slide-flimlink'}
            >
                {linkMoreText}
            </FilmRoomLinkWrapper>
        );
};

const VideoThumbsHolder = ({ numberSkeletonSlides }) => {
    return new Array(numberSkeletonSlides).fill(0).map((id, i) => {
        return (
            <SkeletonWrapper key={`skeleton-slide-${i}`}>
                <Skeleton width={`${CARDWIDTH}px`} height="211px" />
            </SkeletonWrapper>
        );
    });
};

const GameStoryPlaceHolder = () => {
    return (
        <StorySkeletonWrapper key={'skeleton-slide-gamestory'}>
            <Skeleton />
        </StorySkeletonWrapper>
    );
};

export const getHighLights = (
    highlights,
    showMetaInfo = true,
    handlers,
    width,
    playerContext
) => {
    return highlights?.map((highlight, index) => {
        const {
            title = '',
            duration = '',
            id,
            templateUrl = '',
            slug,
            playbacks
        } = highlight;

        return (
            <VideoPlayer
                title={title}
                duration={duration}
                playbackContainerId={'video-carousel-' + id}
                key={`video-carousel-${id}-${index}`}
                id={id}
                showMetaInfo={showMetaInfo}
                templateUrl={templateUrl}
                slug={slug}
                showTitle={true}
                width={width}
                handlers={handlers}
                playbacks={playbacks}
                playerContext={playerContext}
            />
        );
    });
};

const GameStoryComp = ({ storySlide, isLoading, storyUrl }) => {
    if (isLoading) {
        return <GameStoryPlaceHolder key='game-story-placeholder'/>;
    } else {
        if (storyUrl === '') {
            return null;
        } else if (typeof storyUrl === 'undefined') {
            return <GameStoryPlaceHolder key='game-story-placeholder'/>;
        } else {
            return storySlide;
        }
    }
};

const VideoCarousel = ({
    highlights = [],
    storyUrl,
    showStory = true,
    showFilmRoomLink = true,
    handlers,
    gamePk,
    deviceInteraction,
    away,
    home,
    sportId = 1,
    isLoading = false,
    numberSkeletonSlides = 4,
    language,
    appPath = '',
    title,
    showSeeAllLink = false,
    width = '362px',
    playerContext = 'Highlights Carousel'
}) => {
    const translate = useTranslate();
    const Tracking = useTracking();
    const dispatch = useDispatch();
    const handleStoryUrl = useCallback(
        (e) => {
            const anchorEl = e?.currentTarget;
            const cfg = {
                componentName: ['Game Story'],
                text: anchorEl.innerText || '',
                activity: ' Link Click'
            };
            Tracking.track.track_click(cfg);

            if (
                deviceInteraction &&
                deviceInteraction.webview &&
                deviceInteraction.webview({ url: storyUrl })
            ) {
                e.preventDefault();
            }
        },
        [deviceInteraction, storyUrl]
    );

    const handlersStoryUrl = useMemo(
        () => ({
            onClick: handleStoryUrl
        }),
        [handleStoryUrl]
    );

    const handleSeeAllClick = useCallback((e) => {
        const cfg = {
            componentName: ['Video Carousel'],
            text: 'See All',
            activity: 'Click'
        };

        Tracking.track.track_click(cfg);
        Tracking.track.track_pageload();

        dispatch(changeViewAction({
            game_tab: 'video',
            game_view: 'final',
            detail: '',
            updateRoute: true
        }));
    }, [dispatch, changeViewAction, Tracking]);

    const linkMoreText = translate(
        'button_more_highlights',
        'See more highlights from this game on MLB Film Room'
    );
    const seeAllText = translate('button_see_all', 'See All');
    const gameStoryText = translate('button_game_story', 'Game Story');
    const highlightsTitleText = title || translate('highlights', 'Highlights');

    // Generate the slides showcasing the game's highlights
    // Re-generate when there's a change to the highlights object or handler passed

    const highlightSlides = useMemo(() => {
        return getHighLights(highlights, true, handlers, width, playerContext);
    }, [isLoading, highlights, handlers]);

    // Generate the Story slide.
    // TODO: Needs design and more work
    const storySlide = useMemo(() => {
        return showStory && storyUrl
            ? generateStorySlide(storyUrl, handlersStoryUrl, gameStoryText, home, away)
            : null;
    }, [isLoading, storyUrl, showStory]);

    // Generate the film room link slide
    const filmRoomLinkEl = useMemo(() => {
        const filmRoomLink = getFilmRoomGameUrl({ gamePk, language, appPath });

        return showFilmRoomLink
            ? generateLinkSlide(isLoading, filmRoomLink, linkMoreText)
            : null;
    }, [isLoading, gamePk, appPath]);

    const hasGameStory = storyUrl !== '';
    const hasVideos = highlights !== null && highlights.length > 0;
    const gsArr = [];
    const gsComp =
        !isLoading && storySlide !== null
            ? (
                storySlide
            )
            : (
                <GameStoryComp
                    key={`${storyUrl}`}
                    storyUrl={storyUrl}
                    isLoading={isLoading}
                    storySlide={storySlide}
                />
            );

    if (showStory && storyUrl !== '') {
        gsArr.push(gsComp);
    }

    const hlArr =
        !isLoading && highlights !== null
            ? (
                highlightSlides
            )
            : (
                <VideoThumbsHolder numberSkeletonSlides={numberSkeletonSlides} />
            );
    const slideArr = gsArr.concat(hlArr);

    if (showFilmRoomLink && sportId === 1) {
        slideArr.push(filmRoomLinkEl);
    }
    const highlightsCount = highlights?.length ?? 0;

    if (slideArr.length === 0) {
        return null;
    }

    return (
        <Fragment>
            <HeaderWrapper>
                <TitleWrapper>{highlightsTitleText}</TitleWrapper>
                { showSeeAllLink && <SeeAllButton
                    shape={ ButtonShapes.HOLLOW_BORDERLESS }
                    hasHover={false}
                    label={seeAllText}
                    handlers={{
                        onClick: handleSeeAllClick
                    }}
                    size={'sm'} />}
            </HeaderWrapper>
            <VideoCarouselWrapper>
                <ControlledOverflow
                    disableShadows
                    childWidth={highlightsCount ? CARDWIDTH : CARDWIDTH_DEFAULT}
                    key={`game-${gamePk}-${highlightsCount}-${hasGameStory}-${hasVideos}-${showFilmRoomLink}`}
                    addEndInsert
                    endInsertWidth={ 17 }
                >
                    {slideArr}
                </ControlledOverflow>
            </VideoCarouselWrapper>
        </Fragment>
    );
};

VideoCarousel.propTypes = {
    highlights: PropTypes.array,
    storyUrl: PropTypes.string,
    showStory: PropTypes.bool,
    showFilmRoomLink: PropTypes.bool,
    highlightClickHandler: PropTypes.func,
    gamePk: PropTypes.number,
    deviceInteraction: PropTypes.object,
    home: PropTypes.number,
    away: PropTypes.number,
    handleTabChange: PropTypes.func,
    isLoading: PropTypes.bool,
    numberSkeletonSlides: PropTypes.number,
    language: PropTypes.string,
    sportId: PropTypes.number,
    appPath: PropTypes.string,
    title: PropTypes.string,
    showSeeAllLink: PropTypes.bool,
    playerContext: PropTypes.string
};

export default withTheme(VideoCarousel);
